import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { alunoService, cursoService, cidadeService } from '@/services/api';
import { PaginationParams, Curso, Cidade, PaginationResponse, Aluno } from '@/types';
import { PlusIcon, PencilIcon, TrashIcon, UserGroupIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

export const AlunosPage: React.FC = () => {
  const [filters, setFilters] = useState<PaginationParams & { curso?: string; cidade?: string }>({
    page: 1,
    limit: 10,
  });
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateAlunos } = useQueryInvalidation();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();

  const [searchText, setSearchText] = useState<string>("");
  const [cursos, setCursos] = useState<Curso[]>([]);
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [cursoQuery, setCursoQuery] = useState<string>("");
  const [cidadeQuery, setCidadeQuery] = useState<string>("");
  const [openCurso, setOpenCurso] = useState<boolean>(false);
  const [openCidade, setOpenCidade] = useState<boolean>(false);
  const cursoRef = useRef<HTMLDivElement | null>(null);
  const cidadeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchText || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchText]);

  useEffect(() => {
    // Carregar opções de cursos e cidades para filtros
    const loadOptions = async () => {
      try {
        const [cursosResp, cidadesResp] = await Promise.all([
          cursoService.list({ page: 1, limit: 1000 }),
          cidadeService.list({ page: 1, limit: 1000 }),
        ]);
        setCursos(cursosResp?.data || []);
        setCidades(cidadesResp?.data || []);
      } catch (e) {
        // falha silenciosa nos filtros opcionais
      }
    };
    loadOptions();
  }, []);

  const filteredCursos = cursos.filter(c =>
    !cursoQuery || c.nome.toLowerCase().includes(cursoQuery.toLowerCase())
  );
  const filteredCidades = cidades.filter(c =>
    !cidadeQuery || c.nome.toLowerCase().includes(cidadeQuery.toLowerCase()) || c.uf.toLowerCase().includes(cidadeQuery.toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(event: globalThis.MouseEvent) {
      const target = event.target as any;
      if (cursoRef.current && target && !cursoRef.current.contains(target)) {
        setOpenCurso(false);
      }
      if (cidadeRef.current && target && !cidadeRef.current.contains(target)) {
        setOpenCidade(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [loadError, setLoadError] = useState<string | null>(null);

  const { data: alunosData, isLoading, error } = useQuery<PaginationResponse<Aluno>>({
    queryKey: ['alunos', filters],
    queryFn: () => alunoService.list(filters),
  });

  useEffect(() => {
    if (error) {
      const message = handleError(error as any, 'Erro ao carregar alunos');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (alunosData) {
      setLoadError(null);
    }
  }, [alunosData]);

  const deleteAlunoMutation = useMutation({
    mutationFn: alunoService.delete,
    onSuccess: (_data, deletedId) => {
      invalidateAlunos(deletedId);
      handleSuccess('Aluno excluído com sucesso!');
    },
    onError: (error: Error) => {
      handleError(error, 'Erro ao excluir aluno');
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id: number) => {
    showConfirm(
      'Excluir Aluno',
      'Tem certeza que deseja excluir este aluno? Esta ação não pode ser desfeita.',
      async () => {
        await deleteAlunoMutation.mutateAsync(id);
      },
      'danger'
    );
  };


  if (loadError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Alunos</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os alunos cadastrados no sistema
          </p>
        </div>
        <Link to="/alunos/novo" className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto">
            <PlusIcon className="h-4 w-4 mr-2" />
            Novo Aluno
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total de Alunos</p>
              <p className="text-2xl font-bold text-gray-900">{alunosData?.pagination?.total || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Alunos */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Lista de Alunos</CardTitle>
              <CardDescription>
                Todos os alunos cadastrados no sistema
              </CardDescription>
            </div>
            <div className="w-full flex flex-col gap-3">
              <div className="w-full">
                <input
                  type="text"
                  placeholder="Buscar por nome ou CPF"
                  className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="w-full sm:flex-1" ref={cursoRef}>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Selecione um curso"
                      className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={cursoQuery || (filters.curso ? String(filters.curso) : "")}
                      onChange={(e) => { setCursoQuery(e.target.value); setOpenCurso(true); }}
                      onFocus={() => setOpenCurso(true)}
                      onKeyDown={(e) => { if (e.key === 'Escape') setOpenCurso(false); }}
                    />
                    {(filters.curso || cursoQuery) && (
                      <button
                        type="button"
                        aria-label="Limpar curso"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => { setFilters(prev => ({ ...prev, page: 1, curso: undefined })); setCursoQuery(""); }}
                      >
                        ×
                      </button>
                    )}
                    {openCurso && (
                      <div
                        className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div className="px-2 py-1 text-xs text-gray-500">Cursos</div>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => { setFilters(prev => ({ ...prev, page: 1, curso: undefined })); setCursoQuery(""); setOpenCurso(false); }}
                        >
                          Todos os cursos
                        </button>
                        {filteredCursos.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-gray-100"
                            onClick={() => { setFilters(prev => ({ ...prev, page: 1, curso: c.nome })); setCursoQuery(""); setOpenCurso(false); }}
                          >
                            {c.nome}
                          </button>
                        ))}
                        {filteredCursos.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">Nenhum curso encontrado</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-full sm:flex-1" ref={cidadeRef}>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Selecione uma cidade"
                      className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={cidadeQuery || (filters.cidade ? String(filters.cidade) : "")}
                      onChange={(e) => { setCidadeQuery(e.target.value); setOpenCidade(true); }}
                      onFocus={() => setOpenCidade(true)}
                      onKeyDown={(e) => { if (e.key === 'Escape') setOpenCidade(false); }}
                    />
                    {(filters.cidade || cidadeQuery) && (
                      <button
                        type="button"
                        aria-label="Limpar cidade"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => { setFilters(prev => ({ ...prev, page: 1, cidade: undefined })); setCidadeQuery(""); }}
                      >
                        ×
                      </button>
                    )}
                    {openCidade && (
                      <div
                        className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div className="px-2 py-1 text-xs text-gray-500">Cidades</div>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => { setFilters(prev => ({ ...prev, page: 1, cidade: undefined })); setCidadeQuery(""); setOpenCidade(false); }}
                        >
                          Todas as cidades
                        </button>
                        {filteredCidades.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-gray-100"
                            onClick={() => { setFilters(prev => ({ ...prev, page: 1, cidade: c.nome })); setCidadeQuery(""); setOpenCidade(false); }}
                          >
                            {c.nome} ({c.uf})
                          </button>
                        ))}
                        {filteredCidades.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">Nenhuma cidade encontrada</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(filters.search || filters.curso || filters.cidade) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSearchText(""); setFilters({ page: 1, limit: filters.limit }); }}
                  className="w-full sm:w-auto"
                >
                  Limpar Filtros
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : alunosData?.data?.length === 0 ? (
            <div className="text-center py-8">
              <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum aluno encontrado</h3>
              <p className="text-gray-500 mb-4">Comece criando um novo aluno.</p>
              <Link to="/alunos/novo">
                <Button asChild>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Novo Aluno
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {alunosData?.data?.map((aluno) => (
                <div
                  key={aluno.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <UserGroupIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                          {aluno.nome}
                        </h3>
                      </div>
                    </div>

                    <div className="flex space-x-2 justify-end sm:justify-start">
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex-1 sm:flex-initial text-blue-600 hover:text-blue-700 hover:border-blue-300"
                        title="Ver detalhes"
                      >
                        <Link to={`/alunos/${aluno.id}`} className="flex items-center justify-center gap-1">
                          <EyeIcon className="h-4 w-4" />
                          <span className="sm:hidden">Ver</span>
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex-1 sm:flex-initial text-yellow-600 hover:text-yellow-700 hover:border-yellow-300"
                        title="Editar"
                      >
                        <Link to={`/alunos/${aluno.id}/editar`} className="flex items-center justify-center gap-1">
                          <PencilIcon className="h-4 w-4" />
                          <span className="sm:hidden">Editar</span>
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(aluno.id)}
                        disabled={deleteAlunoMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:border-red-300 flex-1 sm:flex-initial"
                        title="Excluir"
                      >
                        <div className="flex items-center justify-center gap-1">
                          <TrashIcon className="h-4 w-4" />
                          <span className="sm:hidden">Excluir</span>
                        </div>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Paginação */}
          {alunosData && alunosData.pagination?.totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) - 1)}
                  disabled={(filters.page || 1) <= 1}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 py-2 text-sm text-gray-700">
                  Página {filters.page || 1} de {alunosData.pagination?.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) + 1)}
                  disabled={(filters.page || 1) >= (alunosData.pagination?.totalPages || 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteAlunoMutation.isPending}
      />
    </div>
  );
};